import type { IEntity, IPoint } from "dxf-parser";
import type { IGroup } from "dxf-parser/dist/DxfArrayScanner";
import type DxfArrayScanner from "dxf-parser/dist/DxfArrayScanner";
import type IGeometry from "dxf-parser/dist/entities/geomtry";
import * as helpers from "lib/house/dxf/import/dxfParserLibHelpers";

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface IMLeaderEntity extends IEntity {
  contextData?: MLeaderContextData;
}

export default class MLeader implements IGeometry {
  public ForEntityName = "MLEADER" as any;
  public parseEntity(scanner: DxfArrayScanner, curr: IGroup) {
    return parseMLeaderEntity(scanner, curr);
  }
}

/**
 * MultiLeader is the same as a Leader, but it is generated via the AUTOCAD
 * MULTILEADER command. It can have multiple leader lines pointing to the same
 * text or content.
 */
export class MultiLeader implements IGeometry {
  public ForEntityName = "MULTILEADER" as any;
  public parseEntity(scanner: DxfArrayScanner, curr: IGroup) {
    return parseMLeaderEntity(scanner, curr);
  }
}

function parseMLeaderEntity(scanner: DxfArrayScanner, curr: IGroup) {
  const entity = {
    type: curr.value,
  } as IMLeaderEntity;

  curr = scanner.next();
  while (!scanner.isEOF()) {
    if (curr.code === 0) {
      break;
    }

    switch (curr.code) {
      case 300:
        entity.contextData = parseContextData(scanner, curr);
        break;
      default:
        helpers.checkCommonEntityProperties(entity, curr, scanner);
        break;
    }

    curr = scanner.next();
  }

  return entity;
}
/**
 * @NOTE - We don't handle all poperties. for missing properties see:
 * @link https://help.autodesk.com/view/ACD/2025/ENU/?guid=GUID-EC56D0DE-026D-46AB-87B1-9692393B0C22
 */
interface MLeaderContextData {
  leaders: Leader[];
  arrowHeadSize?: number;
  contentScale?: number;
  contentBasePosition?: IPoint;
  textContents?: string;
  textLocation?: IPoint;
  textRotation: number;
  textWidth: number;
  textHeight: number;
  textColor?: number;
  textAttachmentPoint?: number;
}

function parseContextData(scanner: DxfArrayScanner, curr: IGroup) {
  const contextData = {
    leaders: [],
    textRotation: 0,
    textWidth: 0,
    textHeight: 1,
  } as MLeaderContextData;

  curr = scanner.next();
  while (!scanner.isEOF()) {
    if (curr.code === 0 || curr.code === 301) {
      break;
    }

    switch (curr.code) {
      case 10:
      case 20:
      case 30:
        contextData.contentBasePosition = helpers.parsePoint(scanner);
        break;
      case 40:
        contextData.contentScale = parseFloat(curr.value as string);
        break;
      case 140:
        contextData.arrowHeadSize = parseFloat(curr.value as string);
        break;
      case 41:
        contextData.textHeight = parseFloat(curr.value as string);
        break;
      case 90:
        contextData.textColor = parseInt(curr.value as string, 10);
        break;
      case 12:
      case 22:
      case 32:
        contextData.textLocation = helpers.parsePoint(scanner);
        break;
      case 42:
        contextData.textRotation = parseFloat(curr.value as string);
        break;
      case 43:
        contextData.textWidth = parseFloat(curr.value as string);
        break;
      case 171:
        contextData.textAttachmentPoint = parseInt(curr.value as string, 10);
        break;
      case 304:
        contextData.textContents = curr.value as string;
        break;
      case 302: {
        contextData.leaders.push(parseLeader(scanner, curr));
        break;
      }
      default:
        helpers.checkCommonEntityProperties(contextData as any, curr, scanner);
        break;
    }

    curr = scanner.next();
  }

  return contextData;
}

interface Leader {
  hasLastLeaderLinePoint: boolean;
  lastLeaderLinePoint?: IPoint;

  hasSetDoglegVector: boolean;
  doglegVector?: IPoint;
  doglegLength?: number;

  breakStartPoint?: IPoint;
  breakEndPoint?: IPoint;

  leaderBranchIndex?: number;

  leaderLines: LeaderLine[];
}

function parseLeader(scanner: DxfArrayScanner, curr: IGroup) {
  const leader: Leader = {
    hasLastLeaderLinePoint: false,
    hasSetDoglegVector: false,
    leaderLines: [],
  };

  curr = scanner.next();
  while (!scanner.isEOF()) {
    if (curr.code === 0 || curr.code === 303) {
      break;
    }

    switch (curr.code) {
      case 90:
        leader.leaderBranchIndex = parseInt(curr.value as string, 10);
        break;
      case 290:
        leader.hasLastLeaderLinePoint = curr.value === 1;
        break;
      case 10:
      case 20:
      case 30:
        leader.lastLeaderLinePoint = helpers.parsePoint(scanner);
        break;
      case 40:
        leader.doglegLength = parseFloat(curr.value as string);
        break;
      case 291:
        leader.hasSetDoglegVector = curr.value === 1;
        break;
      case 11:
      case 21:
      case 31:
        leader.doglegVector = helpers.parsePoint(scanner);
        break;
      case 12:
      case 22:
      case 32:
        leader.breakEndPoint = helpers.parsePoint(scanner);
        break;
      case 13:
      case 23:
      case 33:
        leader.breakEndPoint = helpers.parsePoint(scanner);
        break;
      case 304:
        leader.leaderLines.push(parseLeaderLine(scanner, curr));
        break;
      default:
        helpers.checkCommonEntityProperties(leader as any, curr, scanner);
        break;
    }

    curr = scanner.next();
  }

  return leader;
}

interface LeaderLine {
  vertices: IPoint[];
  breakPointIndex?: number;
  breakStartPoint?: IPoint;
  breakEndPoint?: IPoint;
  leaderLineIndex?: number;
}

function parseLeaderLine(scanner: DxfArrayScanner, curr: IGroup) {
  const leaderLine = {
    vertices: [] as IPoint[],
  } as LeaderLine;

  curr = scanner.next();
  while (!scanner.isEOF()) {
    if (curr.code === 0 || curr.code === 305) {
      break;
    }

    switch (curr.code) {
      case 10:
      case 20:
      case 30:
        leaderLine.vertices.push(helpers.parsePoint(scanner));
        break;
      case 90:
        leaderLine.breakPointIndex = parseInt(curr.value as string, 10);
        break;
      case 91:
        leaderLine.leaderLineIndex = parseInt(curr.value as string, 10);
        break;
      case 11:
      case 21:
      case 31:
        leaderLine.breakStartPoint = helpers.parsePoint(scanner);
        break;
      case 12:
      case 22:
      case 32:
        leaderLine.breakEndPoint = helpers.parsePoint(scanner);
        break;
      default:
        helpers.checkCommonEntityProperties(leaderLine as any, curr, scanner);
        break;
    }

    curr = scanner.next();
  }

  return leaderLine;
}
